import type { BoardCard } from '@/api/boardsApi'
import { slaTone } from '@/lib/slaDisplay'

/**
 * The board's quick filters (z8nrz7fy99) and bulk-move rules (z8nrz7fy9b).
 * Quick filters narrow the cards already loaded — instant, and they never
 * change the saved board. They live in the URL so a view can be shared.
 */

export type Who = 'all' | 'me' | 'unassigned'

export interface QuickFilters {
  q: string
  who: Who
  priorities: string[]
  atRisk: boolean
  customer: string
}

export const NO_FILTERS: QuickFilters = { q: '', who: 'all', priorities: [], atRisk: false, customer: '' }

export function filtersFromParams(p: URLSearchParams): QuickFilters {
  const who = p.get('who')
  return {
    q: p.get('q') ?? '',
    who: who === 'me' || who === 'unassigned' ? who : 'all',
    priorities: (p.get('priority') ?? '').split(',').filter((x) => /^P[1-4]$/.test(x)),
    atRisk: p.get('risk') === '1',
    customer: p.get('customer') ?? '',
  }
}

/** Writes the filters onto `base`, keeping its other params (e.g. `ticket` for the side panel). */
export function filtersToParams(f: QuickFilters, base: URLSearchParams = new URLSearchParams()): URLSearchParams {
  const p = new URLSearchParams(base)
  const set = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k))
  set('q', f.q.trim())
  set('who', f.who === 'all' ? '' : f.who)
  set('priority', f.priorities.join(','))
  set('risk', f.atRisk ? '1' : '')
  set('customer', f.customer)
  return p
}

export function activeFilterCount(f: QuickFilters): number {
  return [f.q.trim() !== '', f.who !== 'all', f.priorities.length > 0, f.atRisk, f.customer !== ''].filter(Boolean).length
}

/** SLA at risk: breached, under an hour, or past the policy's warn point (the queue's own tones). */
export function isAtRisk(card: BoardCard, now: number): boolean {
  if (card.status === 'resolved' || card.status === 'closed') return false
  const tone = slaTone(card.sla, now)
  return tone === 'breach' || tone === 'critical' || tone === 'warning'
}

export function filterCards(cards: readonly BoardCard[], f: QuickFilters, meId: string | null, now: number): BoardCard[] {
  const q = f.q.trim().toLowerCase()
  return cards.filter((c) => {
    if (f.who === 'me' && c.assignee?.id !== meId) return false
    if (f.who === 'unassigned' && c.assignee) return false
    if (f.priorities.length && !f.priorities.includes(c.priority.toUpperCase())) return false
    if (f.customer && c.customer?.id !== f.customer) return false
    if (f.atRisk && !isAtRisk(c, now)) return false
    if (q && ![c.key, c.subject, c.customer?.name, c.product?.name, c.assignee?.name].some((v) => v?.toLowerCase().includes(q))) return false
    return true
  })
}

/** The customers on the loaded cards, by name — the options for the customer filter. */
export function customersOf(cards: readonly BoardCard[]): { id: string; name: string }[] {
  const seen = new Map<string, string>()
  for (const c of cards) if (c.customer) seen.set(c.customer.id, c.customer.name)
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * The statuses the selection can be moved to: every card may move there or is
 * already there (it is then skipped), and at least one card actually moves.
 * Resolve is excluded: each ticket needs its own reason in the Resolve dialog.
 */
export function commonMoves(cards: readonly BoardCard[]): string[] {
  if (!cards.length) return []
  const reachable = (c: BoardCard) => [...c.allowedTransitions, c.status]
  const all = cards.slice(1).reduce((acc, c) => acc.filter((s) => reachable(c).includes(s)), reachable(cards[0]))
  return [...new Set(all)].filter((s) => cards.some((c) => c.status !== s))
}
